import React, {
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { HelpCircle, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";

const CHIP_SLOT_WIDTH = 96;
const ROW_HEIGHT = 26;
const ROW_GAP = 4;
const COLUMN_GAP = 6;
const GRID_MAX_HEIGHT = 320;
const DEFAULT_SEPARATOR_RE = /[,;\s]+/;
const TOAST_INVALID_LIST_LIMIT = 5;

export interface BadgeListControlProps {
  label: string;
  values: string[];
  onChange: (values: string[]) => void;
  /** Returns normalized value, or null if invalid. */
  validateEntry: (input: string) => string | null;
  disabled?: boolean;
  addPlaceholder?: string;
  /** Replaces the default tooltip line about accepted separators. */
  tooltipText?: string;
  /** Extra line appended in the help tooltip. */
  helpText?: string;
  /** Short noun used in toasts, e.g. "case numbers", "GUIDs". */
  entityLabel?: string;
  /**
   * Regex used to split typed/pasted input and lines of imported files
   * into tokens. Default `[,;\s]+`. Override to preserve characters
   * inside a single entry (e.g. spaces in folder names).
   */
  separator?: RegExp;
  /** Whether to offer Import from a file. Default true. */
  importable?: boolean;
  /** Width each chip is laid out in, in pixels; raise it for long entries so a row does not clip its last chips. */
  chipSlotWidth?: number;
  labelClassName?: string;
  /** Rendered after the add box, such as a button that picks entries some other way. */
  inputAddon?: React.ReactNode;
  /** How the confirmations name the list, e.g. "the case list". Default: the label in lower case. */
  listName?: string;
}

function useContainerWidth() {
  const [width, setWidth] = useState(0);
  const observerRef = useRef<ResizeObserver | null>(null);
  const setRef = useCallback((node: HTMLDivElement | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;
    if (!node) return;
    setWidth(node.getBoundingClientRect().width);
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) setWidth(entry.contentRect.width);
    });
    ro.observe(node);
    observerRef.current = ro;
  }, []);
  return [width, setRef] as const;
}

interface ParseResult {
  valid: string[];
  invalidLines: number[];
}

function parseImport(
  text: string,
  validateEntry: (input: string) => string | null,
  separator: RegExp,
): ParseResult {
  const valid: string[] = [];
  const invalidLines: number[] = [];
  const lines = text.split(/\r?\n/);
  lines.forEach((line, idx) => {
    const tokens = line
      .split(separator)
      .map((t) => t.trim())
      .filter((t) => t !== "");
    if (tokens.length === 0) return;
    let lineHadInvalid = false;
    tokens.forEach((tok) => {
      const normalized = validateEntry(tok);
      if (normalized) valid.push(normalized);
      else lineHadInvalid = true;
    });
    if (lineHadInvalid) invalidLines.push(idx + 1);
  });
  return { valid, invalidLines };
}

interface ChipCellProps {
  value: string;
  onRemove: (value: string) => void;
  disabled: boolean;
  isScrolling: boolean;
}

const ChipCell = React.memo<ChipCellProps>(
  ({ value, onRemove, disabled, isScrolling }) => (
    <span className="inline-flex items-center gap-0.5 min-w-0 shrink-0">
      <span
        title={value}
        className="inline-flex items-center px-2 h-[22px] text-xs font-medium leading-none rounded-md bg-primary/15 dark:bg-primary/25 text-primary truncate min-w-0 max-w-[160px]"
      >
        {value}
      </span>
      {!disabled &&
        // Skip the remove button during active scroll: keeps row-mount cheap
        // (no <button>/<svg>/<path> fibers). It rematerializes when scrolling
        // stops, via React.memo re-render on isScrolling toggle.
        (isScrolling ? (
          <span
            aria-hidden="true"
            className="inline-block size-5 shrink-0"
          />
        ) : (
          <button
            type="button"
            onClick={(e) => {
              e.preventDefault();
              onRemove(value);
            }}
            aria-label={`Remove ${value}`}
            className="inline-flex items-center justify-center size-5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
          >
            <svg
              width="9"
              height="9"
              viewBox="0 0 10 10"
              aria-hidden="true"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
            >
              <path d="M1.5 1.5l7 7M8.5 1.5l-7 7" />
            </svg>
          </button>
        ))}
    </span>
  ),
);
ChipCell.displayName = "ChipCell";

const BadgeListControl: React.FC<BadgeListControlProps> = ({
  label,
  values,
  onChange,
  validateEntry,
  disabled = false,
  addPlaceholder = "Type or paste, then press Enter…",
  tooltipText,
  helpText,
  entityLabel = "entries",
  separator = DEFAULT_SEPARATOR_RE,
  importable = true,
  chipSlotWidth = CHIP_SLOT_WIDTH,
  labelClassName,
  inputAddon,
  listName,
}) => {
  const [searchInput, setSearchInput] = useState("");
  const deferredSearch = useDeferredValue(searchInput);
  const trimmedSearch = deferredSearch.trim();

  const parentRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [containerWidth, setContainerRef] = useContainerWidth();
  const setScrollContainerRef = useCallback(
    (node: HTMLDivElement | null) => {
      parentRef.current = node;
      setContainerRef(node);
    },
    [setContainerRef],
  );

  const [clearDialogOpen, setClearDialogOpen] = useState(false);
  const [removeDialogOpen, setRemoveDialogOpen] = useState(false);

  // Stable ref so chip-row callbacks don't need `values` in their deps
  // (which would invalidate React.memo on every list mutation).
  const valuesRef = useRef(values);
  useEffect(() => {
    valuesRef.current = values;
  }, [values]);

  const filtered = useMemo(() => {
    if (!trimmedSearch) return values;
    const lower = trimmedSearch.toLowerCase();
    return values.filter((v) => v.toLowerCase().includes(lower));
  }, [values, trimmedSearch]);

  const cols = Math.max(
    1,
    Math.floor(((containerWidth || 600) - 12) / (chipSlotWidth + COLUMN_GAP)),
  );
  const rowCount = Math.ceil(filtered.length / cols);

  const virtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => parentRef.current,
    estimateSize: () => ROW_HEIGHT + ROW_GAP,
    overscan: 20,
  });

  const handleAddTokens = useCallback(
    (input: string) => {
      const tokens = input
        .split(separator)
        .map((t) => t.trim())
        .filter((t) => t !== "");
      if (tokens.length === 0) return;
      const validated = tokens
        .map(validateEntry)
        .filter((v): v is string => v !== null);
      if (validated.length === 0) return;
      const merged = Array.from(new Set([...valuesRef.current, ...validated]));
      if (merged.length !== valuesRef.current.length) onChange(merged);
    },
    [separator, validateEntry, onChange],
  );

  const handleRemove = useCallback(
    (value: string) => {
      onChange(valuesRef.current.filter((v) => v !== value));
    },
    [onChange],
  );

  const handleClearAll = useCallback(() => {
    const count = valuesRef.current.length;
    onChange([]);
    setClearDialogOpen(false);
    toast.success(`Cleared ${count.toLocaleString()} ${entityLabel}`);
  }, [onChange, entityLabel]);

  const handleRemoveMatching = useCallback(() => {
    if (!trimmedSearch) return;
    const lower = trimmedSearch.toLowerCase();
    const toKeep = valuesRef.current.filter(
      (v) => !v.toLowerCase().includes(lower),
    );
    const removedCount = valuesRef.current.length - toKeep.length;
    onChange(toKeep);
    setRemoveDialogOpen(false);
    setSearchInput("");
    toast.success(
      `Removed ${removedCount.toLocaleString()} ${entityLabel} matching "${trimmedSearch}"`,
    );
  }, [trimmedSearch, onChange, entityLabel]);

  const handleFileChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      e.target.value = ""; // allow re-uploading same file
      if (!file) return;
      try {
        const text = await file.text();
        const { valid, invalidLines } = parseImport(text, validateEntry, separator);
        if (valid.length === 0 && invalidLines.length === 0) {
          toast.warning("File contained no entries");
          return;
        }
        const before = valuesRef.current.length;
        const merged = Array.from(new Set([...valuesRef.current, ...valid]));
        const added = merged.length - before;
        const duplicates = valid.length - added;
        if (added > 0) onChange(merged);

        const parts: string[] = [];
        if (added > 0) parts.push(`Imported ${added.toLocaleString()} new`);
        if (duplicates > 0)
          parts.push(`${duplicates.toLocaleString()} duplicates skipped`);
        if (invalidLines.length > 0) {
          const shown = invalidLines
            .slice(0, TOAST_INVALID_LIST_LIMIT)
            .join(", ");
          const extra =
            invalidLines.length > TOAST_INVALID_LIST_LIMIT
              ? ` (+${invalidLines.length - TOAST_INVALID_LIST_LIMIT} more)`
              : "";
          parts.push(`invalid lines ${shown}${extra}`);
        }
        const message = parts.join(" · ") || "No new entries added";
        if (invalidLines.length > 0 && added === 0) toast.error(message);
        else if (invalidLines.length > 0) toast.warning(message);
        else toast.success(message);
      } catch (err: any) {
        toast.error(`Import failed: ${err?.message ?? "unknown error"}`);
      }
    },
    [separator, validateEntry, onChange],
  );

  return (
    <div className="py-2 -mt-0.5">
      <Label className={cn("flex items-center mb-1", labelClassName)}>
        {label}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <HelpCircle className="ml-1 size-[18px]" />
            </TooltipTrigger>
            <TooltipContent side="right">
              <div className="flex flex-col max-w-[280px] justify-center">
                <span className="text-sm">
                  {tooltipText ??
                    "Accepts entries typed individually or separated by comma, semicolon, space, or newline."}
                </span>
                {helpText && (
                  <span className="text-sm mt-0.5">{helpText}</span>
                )}
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </Label>

      <div className="rounded-md border bg-transparent p-2 space-y-2">
        {/* Add input + Import */}
        {!disabled && (
          <div className="flex gap-2">
            <Input
              placeholder={addPlaceholder}
              aria-label={`Add ${entityLabel}`}
              className="h-8 flex-1 text-sm"
              onPaste={(e) => {
                // A single-line box turns pasted line breaks into spaces, so a pasted column would arrive as one
                // entry wherever a space does not separate entries. Split it here instead, as typed text is on Enter.
                const pasted = e.clipboardData.getData("text");
                if (!/[\r\n]/.test(pasted)) return;
                e.preventDefault();
                const input = e.currentTarget;
                const start = input.selectionStart ?? input.value.length;
                const end = input.selectionEnd ?? input.value.length;
                const value = input.value.slice(0, start) + pasted + input.value.slice(end);
                input.value = "";
                handleAddTokens(value);
              }}
              onBlur={(e) => {
                const value = e.target.value;
                e.target.value = "";
                if (value) handleAddTokens(value);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  const target = e.target as HTMLInputElement;
                  const value = target.value;
                  target.value = "";
                  if (value) handleAddTokens(value);
                }
              }}
            />
            {inputAddon}
            {importable && (
              <>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,.tsv,text/plain,text/csv"
                  className="hidden"
                  onChange={handleFileChange}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Upload className="size-3.5" />
                  Import
                </Button>
              </>
            )}
          </div>
        )}

        {/* Search + counter + bulk actions */}
        {values.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap">
            <Input
              placeholder={`Search ${entityLabel}…`}
              aria-label={`Search ${entityLabel}`}
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="h-7 flex-1 min-w-[140px] text-sm"
            />
            <span className="text-xs text-muted-foreground whitespace-nowrap">
              {trimmedSearch
                ? `Showing ${filtered.length.toLocaleString()} of ${values.length.toLocaleString()}`
                : `${values.length.toLocaleString()} total`}
            </span>
            {!disabled && trimmedSearch && filtered.length > 0 && (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setRemoveDialogOpen(true)}
              >
                Remove {filtered.length.toLocaleString()} matching
              </Button>
            )}
            {!disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setClearDialogOpen(true)}
              >
                <Trash2 className="size-3.5" />
                Clear all
              </Button>
            )}
          </div>
        )}

        {/* Virtualized grid */}
        {values.length > 0 && (
          <div
            ref={setScrollContainerRef}
            className="overflow-y-auto rounded border bg-background"
            style={{ maxHeight: GRID_MAX_HEIGHT }}
          >
            {filtered.length === 0 ? (
              <div className="text-xs text-muted-foreground text-center py-6">
                No matches for &quot;{trimmedSearch}&quot;
              </div>
            ) : (
              <div
                style={{
                  height: virtualizer.getTotalSize(),
                  width: "100%",
                  position: "relative",
                }}
              >
                {virtualizer.getVirtualItems().map((virtualRow) => {
                  const startIdx = virtualRow.index * cols;
                  return (
                    <div
                      key={virtualRow.key}
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        width: "100%",
                        transform: `translateY(${virtualRow.start}px)`,
                        height: ROW_HEIGHT + ROW_GAP,
                        padding: "2px 6px",
                        display: "flex",
                        flexWrap: "nowrap",
                        gap: COLUMN_GAP,
                        alignItems: "center",
                        boxSizing: "border-box",
                        overflow: "hidden",
                        contain: "layout paint style",
                        willChange: "transform",
                      }}
                    >
                      {Array.from({ length: cols }).map((_, colIdx) => {
                        const val = filtered[startIdx + colIdx];
                        if (val === undefined) return null;
                        return (
                          <ChipCell
                            key={val}
                            value={val}
                            onRemove={handleRemove}
                            disabled={disabled}
                            isScrolling={virtualizer.isScrolling}
                          />
                        );
                      })}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Confirm clear all */}
      <Dialog open={clearDialogOpen} onOpenChange={setClearDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Clear all {entityLabel}?</DialogTitle>
            <DialogDescription>
              Remove all {values.length.toLocaleString()} {entityLabel} from{" "}
              {listName ?? label.toLowerCase()}. This cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setClearDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleClearAll}
            >
              Clear {values.length.toLocaleString()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm remove matching */}
      <Dialog open={removeDialogOpen} onOpenChange={setRemoveDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove matching {entityLabel}?</DialogTitle>
            <DialogDescription>
              Remove {filtered.length.toLocaleString()} {entityLabel} matching
              &quot;{trimmedSearch}&quot;.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setRemoveDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              onClick={handleRemoveMatching}
            >
              Remove {filtered.length.toLocaleString()}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BadgeListControl;
